import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeSpec, RequiredSockets } from './NodeSpec';
import type { NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { FlowNodeInput } from '../../sockets/FlowNodeInput';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

export const eventSwitchUiNodeSpec: NodeSpec<NodeCfg<'eventSwitch'>> = {
    requiredSockets: () => {
        const ret: ReturnType<RequiredSockets<'eventSwitch', NodeSocketKindMap['eventSwitch']>> = {
            required: [ 'event', 'condition' ],
            conditionallyRequired: [
                {
                    kind: 'or',
                    availableOptions: [ 'false', 'true' ],
                },
            ],
        };
        return ret;
    },
    cfg: () => ({
        disabled: false,
        id: getUID(),
        kind: 'eventSwitch',
        width: 210,
        controlValues: { conditionInputs: 1, logicalOperator: 'and' },
    }),
    controls: nodeCfg => {
        return {
            conditionInputs: defineControlOpts({
                cfg: {
                    kind: 'button',
                    useDialog: null,
                    label: 'Add Input',
                },
                on: {
                    'update:value': () => {}, // no-op, this control does not use the update:value event
                    'click': (_, { flowEventEmitter }) => {
                        flowEventEmitter.emit('updateNodeCfg', {
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    conditionInputs: nodeCfg.controlValues.conditionInputs + 1,
                                },
                            },
                        });
                    },
                },
                index: 4,
                value: nodeCfg.controlValues.conditionInputs,
            }),
            logicalOperator: defineControlOpts({
                cfg: {
                    kind: 'selection',
                    label: 'Operator',
                    items: [
                        { value: 'and', label: 'AND' },
                        { value: 'or', label: 'OR' },
                    ],
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
                index: 3,
                value: nodeCfg.controlValues.logicalOperator,
            }),
        };
    },
    inputs: nodeCfg => {
        const eventInput: FlowNodeInput.Opts<'event'> = { socket: 'event', label: 'Event', index: 1 };
        const firstConditionInput: FlowNodeInput.Opts<'varValue'> = { socket: 'varValue', label: 'Condition', index: 5 };

        const inputs: ReturnType<NodeSpec<NodeCfg<'eventSwitch'>>['inputs']> = {
            event: eventInput,
            condition: firstConditionInput,
        };
        for (let i = 1; i < nodeCfg.controlValues.conditionInputs; i++) {
            const controlOpts = defineControlOpts({
                cfg: {
                    kind: 'deletableSocket',
                    label: 'Condition',
                },
                on: {
                    'update:value': () => {}, // no-op, this control does not use the update:value event
                    'delete': (_, { flowEventEmitter }) => {
                        flowEventEmitter.emit('removeNodeSocket', {
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    conditionInputs: nodeCfg.controlValues.conditionInputs - 1,
                                },
                            },
                            direction: 'input',
                            removalMethod: {
                                index: i,
                                prefix: 'input',
                                kind: 'indexedIdentifier',
                            },
                        });
                    },
                },
                value: null,
                enableWithConnectedSocket: true,
            });
            inputs[`condition${i}`] = ({
                controlOpts,
                socket: 'varValue',
                index: 5,
            });
        }
        return inputs;
    },
    nodeCategory: 'events',
    kindLabel: 'Event Switch',
    outputs: () => ({
        false: {
            socket: 'event',
            label: 'False',
            index: 2,
        },
        true: {
            socket: 'event',
            label: 'True',
            index: 0,
        },
    }),
    validator: () => true,
};
